import type { ReportModuleAPI } from './reportModuleApi.ts'

/**
 * A report-version factory: dynamically imports one report module, resolving to its `ReportModuleAPI`
 * default export. Both the live module (`@modules/report`) and an archived copy
 * (`@report-versions/<id>`, id `YYYY-MM-DD-HHmm`) export a `default` satisfying `ReportModuleAPI`.
 */
export type ReportVersionFactory = () => Promise<{ default: ReportModuleAPI }>

export type ReportVersionData = {
  mod: () => Promise<{ default: ReportModuleAPI }>
  productVersion: string
}

// Maps a result's CMS content-snapshot SHA-256 to the report version that renders it,
// or an archived report-version id. Absent snapshots serve live and log a warning.
// Build-time registry of loadable report-version modules: version id → static `import()` factory
// (Vite bundles each as its own lazy chunk). The snapshot map (`reportSnapshotMapping.ts`) decides
// WHICH version renders a snapshot; this provides HOW to load it. Keys: `current` (live renderer) and
// one `YYYY-MM-DD-HHmm` per archived `report-versions/<id>/` copy, appended by hand at archive time.
export const reportSnapshotMap: Record<string, ReportVersionData> = {
  // Pinned: snapshots broken by a later release, served by the archived version 2026-07-14-0824.
  e7559e4cfa8bf8c8927e4a0fbb905c7913f6750511c987dc4f9a9756e2bad3ca: {
    productVersion: 'V0.7',
    mod: () => import('@report-versions/2026-07-14-0824')
  },
  '76a638a4cbbc69b5ffb456f82db807894d55f78ed70eed255a627e0e7f5c3c3a': {
    productVersion: 'V0.7',
    mod: () => import('@report-versions/2026-07-14-0824')
  },
  bd1d181189e878b490e0675ffa90e980415cfd5b065dc3573461154ce03c83d2: {
    productVersion: 'V0.7',
    mod: () => import('@report-versions/2026-07-14-0824')
  },
  f2108a276935bca281fdf1f616ebb5d1de9dde597e81bd4d289894a52b8e964b: {
    productVersion: 'V0.7',
    mod: () => import('@report-versions/2026-07-14-0824')
  },
  '919f91f21fc74a7f1b033ed1f2b5a7783bdcf4b2def29722763590d234eec889': {
    productVersion: 'V0.7',
    mod: () => import('@report-versions/2026-07-14-0824')
  },
  a7801dc6743fbb754fe5a2a905fca879f39e213999a5674c57f32638cd9bd546: {
    productVersion: 'V0.7',
    mod: () => import('@report-versions/2026-07-14-0824')
  },
  e1f44732c22ce475e9cbfca61cfff86d6254fed10b2c64edc14f910710e48dca: {
    productVersion: 'V0.7',
    mod: () => import('@report-versions/2026-07-14-0824')
  },

  // "latest": assessed and still served by the live renderer.
  '4acade4c1eda1441d3d9bb20e793e41d12889a2cb8dc746847d583f6442ff359': {
    productVersion: 'v1.0',
    mod: () => import('@report-versions/2026-07-29-0428')
  },
  a8002cbda5fd7f29ef85beb14f265cbb7a94c69f18ddc178d2fe11770cc85d29: {
    productVersion: 'v1.0',
    mod: () => import('@report-versions/2026-07-29-0428')
  },
  bfa15cd02016f2ffd2890f3faa05d0c90be8728993c7b2f4f3efc86e32833d37: {
    productVersion: 'v1.0',
    mod: () => import('@report-versions/2026-07-29-0428')
  },
  '9144975cef79474e921f1a7d4df489e3dd9809ad6d77d955f364fb6aa9610d83': {
    productVersion: 'v1.0',
    mod: () => import('@report-versions/2026-07-29-0428')
  },

  '353c53fbd91f2ed6442cf740c465813ae68ed0712459560b91fead9335d0a371': {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  '1845aa135a44b1423cfa750251e14c36d204df163898948bf8895b645b255adb': {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  '245de972b335295bf51f6d78d9b3dd30e75fa5abe29ba46ca1800dfa3547257b': {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  ef8cf2e9acf0a0a44922193ff5ef954c42ec66303c2233fde3762102a444965c: {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  '9440af5f77676a6738d1c3360de6704eac7057d715c687e9b52d6bf67e856e71': {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  '9251ac57ae983d72138be195a8639d733db49f3909e33ad869bac4681e0544d1': {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  '5497b118a2d1cdd91d8a1ee7e358485eae9b401c562ed2750a514c37ecd1be2b': {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  '0b879c7354d88fe365a88a7e298c9bff759da83883964ad15156d35b292ae89f': {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  '39846e5a1dc27704628e5af7c2714e909cfd94041a63df3a5d051365ba70c9c3': {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  '08d861979b848618db7009aa7f31aa92c830b0768e884f679b21ab56793c4961': {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  '577d2cff9bdeec2d1b299cb33a6c17d42366d70196369d456c39c43cef8204fe': {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  eb53dbc530f0877df2bae3bf9a68769816b40c6406b84f05ed2bbcd838f98e63: {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  '8c84830d12d944ce1f0e7f87c754ecd4bcc3853ed1925f9b7b35bac83d3f9958': {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  '1209ae7741afd265e83f7ed9233bac65e8bc9dc3332c39b556b874160d7cea32': {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  d66fe286f4ca49c1a9a3c2e4b06e8ea00d12bb77b0c7f0eb8eea0a2138ae3d65: {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  cf1ad70fadc99c2f7fd75cf48b2fdd75271b3f03370dc551a4999d1cd039a25a: {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  e803767a972ceba4ee4768753c881239de3b1418a867c1241f91e63e4bc0538e: {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  '97149f80b66de914ba2e670bf8f5dcf8d93c8682cdbfee7bf468a44efea8af32': {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  d1fa8184eb6c0361d8233e892ec11a53098df2bbaff864e58815554d7d488e24: {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  '61d520bacc3589926f28ec8b74813df897d95ed2dfa4803a396d5761ce9b510a': {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  '44bcef0d9671772fc21ef66892b83d7acdcc5e403c3ec0cfc62292d74f83472b': {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  cf53e348f414d36a3e8a9d51064aeee54be7274e2dbb658e5abb866ddea10a1b: {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  '16c3025fbe9da8b416110a415a7d7e42cf589d190c1ef7afbd7790be015a55de': {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  '0e0abb556f720dc70eda0632c1e2cc7b3c818e7deb011fdb07c778b1672ccfac': {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  '30d9ca53824cec36e80a993553fb0fcfb1417097aaa407cc7b8a8d356c9aa58e': {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  },
  '3b546e860fa2520ae91d20060785a4c0040cc9edbeeaffcb9bf9db6fbd55f3f7': {
    productVersion: 'v1.1',
    mod: () => import('@modules/report')
  }
}
